// SCR-FND-01 Sign in (frozen P0 mockup). No self sign-up: the owner creates every account (GEN-20).
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Field, Input, Notice } from '../../design/kit';
import { Refusal, signIn } from '../../data/auth';
import { messageKey } from '../../lib/errors';

export function SignIn({
  onSignedIn,
  initialRefusal,
}: {
  onSignedIn: () => void;
  initialRefusal?: string | undefined;
}) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | undefined>(initialRefusal);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setRefusal(undefined);
    try {
      await signIn(email, password);
      onSignedIn();
    } catch (err) {
      setRefusal(err instanceof Refusal ? err.code : 'SERVER_ERROR');
    } finally {
      setBusy(false);
    }
  };

  const tone = refusal === 'OFFLINE' ? 'warning' : 'danger';
  return (
    <main className="yv-auth">
      <form className="yv-auth__panel" onSubmit={submit} noValidate>
        <div className="yv-brand">
          <span className="yv-brand__mark" aria-hidden="true">
            Y
          </span>
          YakVender
        </div>
        <h1 className="yv-auth__title">{t('s1.title')}</h1>
        {refusal && (
          <Notice tone={tone} icon={refusal === 'OFFLINE' ? 'wifiOff' : 'alert'}>
            {t(messageKey(refusal))}
          </Notice>
        )}
        <Field label={t('email')}>
          {({ id, describedBy }) => (
            <Input
              id={id}
              type="email"
              dir="ltr"
              inputMode="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-describedby={describedBy}
            />
          )}
        </Field>
        <Field label={t('password')}>
          {({ id, describedBy }) => (
            <div className="yv-input-row">
              <Input
                id={id}
                type={show ? 'text' : 'password'}
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                aria-describedby={describedBy}
              />
              <Button onClick={() => setShow((v) => !v)} aria-pressed={show}>
                {t('show')}
              </Button>
            </div>
          )}
        </Field>
        <Button type="submit" variant="main" block busy={busy} disabled={!email || !password}>
          {t('signin')}
        </Button>
        <p className="yv-auth__lead">{t('signin.help')}</p>
      </form>
    </main>
  );
}
